import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { API_ENDPOINTS } from '../config/api';
import BlogCardLarge from '../components/BlogCardLarge';
import BlogCardMedium from '../components/BlogCardMedium';
import BlogCardSmall from '../components/BlogCardSmall';
import BlogPreviewCard from '../components/BlogPreviewCard';
import BlogPreviewColumns from '../components/BlogPreviewColumns';
import BlogCardOverflow from '../components/BlogCardOverflow';
import { Loader2 } from 'lucide-react';

const HomePage = () => {
  // Fetch featured blogs (need 17 total)
  const { data: featuredData, isLoading: featuredLoading } = useQuery({
    queryKey: ['blogs', 'featured'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { filter: 'featured', limit: 17 },
      });
      return response.data;
    },
  });

  // Fetch trending blogs (need 19 total)
  const { data: trendingData, isLoading: trendingLoading } = useQuery({
    queryKey: ['blogs', 'trending'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { filter: 'trending', limit: 19 },
      });
      return response.data;
    },
  });

  // Fetch latest blogs by category (need 19 each)
  const categories = ['Business', 'Technology', 'Innovation', 'Health'];
  
  const businessQuery = useQuery({
    queryKey: ['blogs', 'category', 'Business'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: 'Business', limit: 19 },
      });
      return { category: 'Business', ...response.data };
    },
  });

  const technologyQuery = useQuery({
    queryKey: ['blogs', 'category', 'Technology'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: 'Technology', limit: 19 },
      });
      return { category: 'Technology', ...response.data };
    },
  });

  const innovationQuery = useQuery({
    queryKey: ['blogs', 'category', 'Innovation'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: 'Innovation', limit: 19 },
      });
      return { category: 'Innovation', ...response.data };
    },
  });

  const healthQuery = useQuery({
    queryKey: ['blogs', 'category', 'Health'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: 'Health', limit: 19 },
      });
      return { category: 'Health', ...response.data };
    },
  });

  // Fetch latest blogs (need 19 total)
  const latestQuery = useQuery({
    queryKey: ['blogs', 'latest'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { limit: 19 },
      });
      return response.data;
    },
  });

  const categoryResults = [businessQuery, technologyQuery, innovationQuery, healthQuery];

  if (featuredLoading || trendingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const featuredBlogs = featuredData?.blogs || [];
  const trendingBlogs = trendingData?.blogs || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Stories Section */}
        {featuredBlogs.length > 0 && (
          <section className="mb-16">
            <h2 className="text-3xl font-heading font-bold text-heading dark:text-white mb-8">
              Featured Stories
            </h2>
            
            {/* First Section: 3 columns layout (3 cards | 1 big card | 3 cards) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* First Column: 3 blog cards */}
              <div className="flex flex-col space-y-0 md:order-1 order-2">
                {featuredBlogs.slice(0, 3).map((blog) => (
                  <BlogPreviewCard key={blog._id} blog={blog} />
                ))}
              </div>

              {/* Second Column: 1 big blog card - appears first on mobile */}
              <div className="flex flex-col md:order-2 order-1">
                {featuredBlogs[3] && (
                  <div className="h-full">
                    <BlogCardLarge blog={featuredBlogs[3]} />
                  </div>
                )}
              </div>

              {/* Third Column: 3 blog cards */}
              <div className="flex flex-col space-y-0 md:order-3 order-3">
                {featuredBlogs.slice(4, 7).map((blog) => (
                  <BlogPreviewCard key={blog._id} blog={blog} />
                ))}
              </div>
            </div>

            {/* Second Section: 3 columns with 9 preview cards */}
            {featuredBlogs.length > 7 && (
              <BlogPreviewColumns blogs={featuredBlogs.slice(7, 16)} />
            )}
          </section>
        )}

        {/* Trending Now Section - 19 blogs */}
        {trendingBlogs.length > 0 && (
          <section className="mb-16">
            <h2 className="text-3xl font-heading font-bold text-heading dark:text-white mb-8">
              Trending Now
            </h2>
            
            {/* First Row: 3 cards with overflow (showing 4th) */}
            <BlogCardOverflow blogs={trendingBlogs.slice(0, 10)} showCount={3} />

            {/* Second Row: 3 columns with 9 preview cards */}
            {trendingBlogs.length > 10 && (
              <BlogPreviewColumns blogs={trendingBlogs.slice(10, 19)} />
            )}
          </section>
        )}

        {/* Category Sections - 19 blogs each */}
        {categoryResults.map(({ data, isLoading }, idx) => {
          if (isLoading) return null;
          const blogs = data?.blogs || [];
          if (blogs.length === 0) return null;

          return (
            <section key={data?.category || idx} className="mb-16">
              <h2 className="text-3xl font-heading font-bold text-heading dark:text-white mb-8">
                {data?.category}
              </h2>
              
              {/* First Row: 3 cards with overflow */}
              <BlogCardOverflow blogs={blogs.slice(0, 10)} showCount={3} />

              {/* Second Row: 3 columns with 9 preview cards */}
              {blogs.length > 10 && (
                <BlogPreviewColumns blogs={blogs.slice(10, 19)} />
              )}
            </section>
          );
        })}

        {/* Latest Stories Section - 19 blogs */}
        <section className="mb-16">
          <h2 className="text-3xl font-heading font-bold text-heading dark:text-white mb-8">
            Latest Stories
          </h2>
          {latestQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : (
            <>
              {/* First Row: 3 cards with overflow */}
              <BlogCardOverflow blogs={latestQuery.data?.blogs?.slice(0, 10) || []} showCount={3} />

              {/* Second Row: 3 columns with 9 preview cards */}
              {latestQuery.data?.blogs && latestQuery.data.blogs.length > 10 && (
                <BlogPreviewColumns blogs={latestQuery.data.blogs.slice(10, 19)} />
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default HomePage;
